import i18next from "i18next";
import { initReactI18next } from "react-i18next";
import { article } from "./article";
import type { hu } from "./hu";

/** The UI languages: English (the default) and Hungarian. The account's choice is the ``language`` user setting
 *  (Beállítások → Nyelv), stored on the server; the browser keeps only a hint of the last one used, in the
 *  ``breeder_language`` cookie, so the first paint and the Keycloak login page (the server reads the cookie on
 *  ``/bff/login``) speak it before the settings arrive. */
export const LANGUAGES = ["en", "hu"] as const;
export type Language = (typeof LANGUAGES)[number];
const DEFAULT_LANGUAGE: Language = "en";
/** The cookie the server reads too (shared/i18n.py LANGUAGE_COOKIE). */
const HINT_COOKIE = "breeder_language";
/** The request header that tells the server the UI language (shared/i18n.py). */
export const LANGUAGE_HEADER = "X-Breeder-Language";

const isLanguage = (v: unknown): v is Language => LANGUAGES.includes(v as Language);

/** The language this browser last used (the hint), else English. */
function hintedLanguage(): Language {
  const v = new RegExp(`(?:^|;\\s*)${HINT_COOKIE}=([^;]*)`).exec(document.cookie)?.[1];
  return isLanguage(v) ? v : DEFAULT_LANGUAGE;
}

function rememberHint(lang: Language): void {
  const secure = window.location.protocol === "https:" ? "; secure" : "";
  // biome-ignore lint/suspicious/noDocumentCookie: a plain synchronous write; the Cookie Store API is not in every browser
  document.cookie = `${HINT_COOKIE}=${lang}; path=/; max-age=${400 * 24 * 3600}; samesite=lax${secure}`;
}

/** The language the UI speaks now. */
export const currentLanguage = (): Language => (isLanguage(i18next.language) ? i18next.language : DEFAULT_LANGUAGE);

/** Each catalogue is its own chunk: a session downloads only the language it speaks. */
const CATALOGUES: Record<Language, () => Promise<object>> = {
  en: () => import("./en").then((m) => m.en),
  hu: () => import("./hu").then((m) => m.hu),
};

async function load(lang: Language): Promise<void> {
  if (!i18next.hasResourceBundle(lang, "translation")) i18next.addResourceBundle(lang, "translation", await CATALOGUES[lang]());
}

// the page and the hint (the next login page, the server's fallback) follow the language actually shown
i18next.on("languageChanged", (lang) => {
  document.documentElement.lang = lang;
  if (isLanguage(lang)) rememberHint(lang);
});

/** Settled when the first language's texts are in: main.tsx renders after it. */
export const i18nReady: Promise<void> = (async () => {
  const lang = hintedLanguage();
  await i18next.use(initReactI18next).init({
    lng: lang,
    fallbackLng: false, // both catalogues have every key (the Catalog type checks it)
    supportedLngs: [...LANGUAGES],
    resources: { [lang]: { translation: await CATALOGUES[lang]() } },
    partialBundledLanguages: true,
    interpolation: { escapeValue: false }, // React escapes already
    returnNull: false,
  });
  addFormatters();
})();

let requested: Language | undefined;

/** Switch the UI: load the language's texts if needed, then switch; a later switch made while they load wins. */
export async function applyLanguage(lang: Language): Promise<void> {
  requested = lang;
  await i18nReady;
  await load(lang);
  if (requested === lang && i18next.language !== lang) await i18next.changeLanguage(lang);
}

/** Hungarian: "{{v, az}}" → "az 1" / "a 2" / "az MV" / "a B": the article the value takes when read out (article.ts);
 *  "{{v, azStart}}" begins a sentence (i18next lower-cases formatter names, so the two need different names);
 *  "{{v, az(quoted: true)}}" → "az „Alapnézet”": the value in quotes after its article. */
const withArticle =
  (start: boolean) =>
  (value: unknown, _lng: string | undefined, options: { quoted?: unknown }): string => {
    const a = article(value as string | number);
    return `${start ? (a === "az" ? "Az" : "A") : a} ${options?.quoted ? `„${value}”` : value}`;
  };

/** English plurals (Hungarian never inflects after a number): "{{n, count(one: line; other: lines)}}" → "1 line" /
 *  "3 lines"; "{{n, plural(one: line; other: lines)}}" → the word alone. */
type PluralOptions = { one?: string; other?: string };
// the count may come formatted ("1 234", "1,234"): only exactly one takes the singular
const isOne = (value: unknown) => (typeof value === "number" ? value : Number(String(value).replace(/[\s,]/g, ""))) === 1;
const word = (value: unknown, options: PluralOptions) => (isOne(value) ? options.one : options.other) ?? "";

function addFormatters(): void {
  i18next.services.formatter?.add("az", withArticle(false));
  i18next.services.formatter?.add("azStart", withArticle(true));
  i18next.services.formatter?.add("plural", (value: unknown, _lng: string | undefined, options: PluralOptions) => word(value, options));
  i18next.services.formatter?.add(
    "count",
    (value: unknown, _lng: string | undefined, options: PluralOptions) => `${value} ${word(value, options)}`,
  );
}

export const t = i18next.t.bind(i18next);

/** The shared input hint (placeholder / help) in the current language. */
export const hint = (key: keyof typeof hu.inputHints): string => t(`inputHints.${key}`);
export default i18next;
